import type { FormItem } from '#/common/components/Form';
import type { Key } from '#/types';
import type { Field } from '#/common/components/Form';
import { gql } from '@apollo/react-hooks';
import { getAsset, getAssetList } from '#/graphql/queries';

const allFields: Omit<Record<Key<ShowroomDataType>, Field<ShowroomDataType>>, '__typename'> = {
  id: {
    dataField: 'id',
    label: 'ID',
    editorOptions: { readOnly: true },
  },
  tag: {
    dataField: 'tag',
    label: 'Test tag',
    isRequired: true,
  },
  // DevExtreme components
  autocompleteField: {
    dataField: 'autocompleteField',
    label: 'Autocomplete',
    editorType: 'Autocomplete',
    editorOptions: {
      placeholder: 'Type to search...',
      dataSource: ['Apple', 'Banana', 'Cherry', 'Date', 'Elderberry'],
      minSearchLength: 2,
    },
  },
  calendarField: {
    dataField: 'calendarField',
    label: 'Calendar',
    editorType: 'Calendar',
    editorOptions: {
      showTodayButton: true,
      zoomLevel: 'month',
    },
  },
  checkBoxField: {
    dataField: 'checkBoxField',
    label: 'CheckBox',
    editorType: 'CheckBox',
    editorOptions: {
      text: 'Enable this option',
    },
  },
  colorBoxField: {
    dataField: 'colorBoxField',
    label: 'ColorBox',
    editorType: 'ColorBox',
    editorOptions: {
      placeholder: 'Select a color',
    },
  },
  currencyField: {
    dataField: 'currencyField',
    label: 'Currency',
    editorType: 'Currency',
    editorOptions: {
      placeholder: 'Enter amount',
      min: 0,
      step: 0.01,
      format: {
        type: 'currency',
        precision: 2,
      },
      showSpinButtons: true,
    },
  },
  dateBoxField: {
    dataField: 'dateBoxField',
    label: 'DateBox',
    editorType: 'DateBox',
    editorOptions: {
      type: 'date',
      placeholder: 'Select date',
      showClearButton: true,
    },
  },
  dateTimeBoxField: {
    dataField: 'dateTimeBoxField',
    label: 'DateTime Box',
    editorType: 'DateBox',
    editorOptions: {
      type: 'datetime',
      placeholder: 'Select date and time',
      showClearButton: true,
    },
  },
  timeBoxField: {
    dataField: 'timeBoxField',
    label: 'Time Box',
    editorType: 'DateBox',
    editorOptions: {
      type: 'time',
      placeholder: 'Select time',
      showClearButton: true,
      displayFormat: 'HH:mm',
    },
  },
  dropDownBoxField: {
    dataField: 'dropDownBoxField',
    label: 'DropDownBox',
    editorType: 'DropDownBox',
    editorOptions: {
      placeholder: 'Select an option',
      dataSource: [
        { text: 'Option 1', value: 'opt1' },
        { text: 'Option 2', value: 'opt2' },
        { text: 'Option 3', value: 'opt3' },
      ],
      displayExpr: 'text',
      valueExpr: 'value',
      showClearButton: true,
      contentTemplate: (data: any) => {
        const container = document.createElement('div');

        data.component.option('dataSource').forEach((item: any) => {
          const div = document.createElement('div');
          div.className = 'dx-item dx-list-item';
          div.textContent = item.text;

          div.onclick = () => {
            data.component.option('value', item.value);
            data.component.close();
          };

          container.appendChild(div);
        });

        return container;
      },
    },
  },
  htmlEditorField: {
    dataField: 'htmlEditorField',
    label: 'HTML Editor',
    editorType: 'HtmlEditor',
    editorOptions: {
      height: 200,
      toolbar: {
        items: [
          'bold',
          'italic',
          'underline',
          'separator',
          'alignLeft',
          'alignCenter',
          'alignRight',
        ],
      },
    },
  },
  imageField: {
    dataField: 'imageField',
    label: 'Image',
    editorType: 'Image',
    editorOptions: {
      height: '200px',
      alt: 'Selected image',
    },
    cssClass: 'hide-label',
  },
  lookupField: {
    dataField: 'lookupField',
    label: 'Lookup',
    editorType: 'Lookup',
    editorOptions: {
      placeholder: 'Select from lookup',
      dataSource: [
        { id: 1, name: 'John Doe' },
        { id: 2, name: 'Jane Smith' },
        { id: 3, name: 'Bob Johnson' },
      ],
      displayExpr: 'name',
      valueExpr: 'id',
      searchEnabled: true,
    },
  },
  numberBoxField: {
    dataField: 'numberBoxField',
    label: 'NumberBox',
    editorType: 'NumberBox',
    editorOptions: {
      placeholder: 'Enter a number',
      min: 0,
      max: 1000,
      step: 1,
      showSpinButtons: true,
    },
  },
  radioGroupField: {
    dataField: 'radioGroupField',
    label: 'RadioGroup',
    editorType: 'RadioGroup',
    editorOptions: {
      dataSource: ['Option A', 'Option B', 'Option C'],
      layout: 'horizontal',
    },
  },
  rangeSliderField: {
    dataField: 'rangeSliderField',
    label: 'RangeSlider',
    editorType: 'RangeSlider',
    editorOptions: {
      min: 0,
      max: 100,
      start: 20,
      end: 80,
      step: 5,
      tooltip: {
        enabled: true,
        showMode: 'onHover',
      },
    },
  },
  relatedItemsField: {
    dataField: 'relatedItemsField',
    label: 'Related Assets',
    editorType: 'RelatedItems',
    editorOptions: {
      placeholder: 'Search assets...',
      button: {
        tooltip: 'View asset details',
        url: '/assets/:value/details',
        desktopOnly: false,
      },
      displayExpr: 'assetTag',
      valueExpr: 'id',
      dataStoreOptions: {
        getItemList: gql(getAssetList),
        getItem: gql(getAsset),
        filter: ['state', 'notcontains', 'retired'],
      },
    },
  },
  selectBoxField: {
    dataField: 'selectBoxField',
    label: 'SelectBox',
    editorType: 'SelectBox',
    editorOptions: {
      placeholder: 'Choose an option',
      dataSource: ['Small', 'Medium', 'Large', 'Extra Large'],
      searchEnabled: true,
    },
  },
  sliderField: {
    dataField: 'sliderField',
    label: 'Slider',
    editorType: 'Slider',
    editorOptions: {
      min: 0,
      max: 100,
      step: 10,
      tooltip: {
        enabled: true,
        showMode: 'onHover',
      },
    },
  },
  switchField: {
    dataField: 'switchField',
    label: 'Switch',
    editorType: 'Switch',
    editorOptions: {
      switchedOnText: 'ON',
      switchedOffText: 'OFF',
    },
  },
  tagBoxField: {
    dataField: 'tagBoxField',
    label: 'TagBox',
    editorType: 'TagBox',
    editorOptions: {
      placeholder: 'Add tags',
      dataSource: ['React', 'TypeScript', 'DevExtreme', 'JavaScript', 'CSS', 'HTML'],
      searchEnabled: true,
      showSelectionControls: true,
    },
  },
  textAreaField: {
    dataField: 'textAreaField',
    label: 'TextArea',
    editorType: 'TextArea',
    editorOptions: {
      placeholder: 'Enter multi-line text',
      height: 100,
      maxLength: 500,
    },
  },
  textBoxField: {
    dataField: 'textBoxField',
    label: 'TextBox',
    editorType: 'TextBox',
    editorOptions: {
      placeholder: 'Enter text here',
      maxLength: 100,
      showClearButton: true,
    },
  },
};

export type ShowroomDataType = {
  __typename: 'ShowroomDataType';
  id: string;
  tag: string;
  // DevExtreme components
  autocompleteField: string;
  calendarField: Date;
  checkBoxField: boolean;
  colorBoxField: string;
  currencyField: number;
  dateBoxField: Date;
  dateTimeBoxField: Date;
  timeBoxField: Date;
  dropDownBoxField: string;
  htmlEditorField: string;
  imageField: string;
  lookupField: string;
  numberBoxField: number;
  radioGroupField: string;
  rangeSliderField: number[];
  relatedItemsField: string;
  selectBoxField: string;
  sliderField: number;
  switchField: boolean;
  tagBoxField: string[];
  textAreaField: string;
  textBoxField: string;
};

const fields: FormItem<ShowroomDataType>[] = [
  {
    colCount: {
      xs: 1,
      sm: 2,
      md: 4,
      lg: 4,
    },
    caption: 'Basic Input Components',
    items: [
      allFields.textBoxField,
      allFields.textAreaField,
      allFields.numberBoxField,
      allFields.currencyField,
    ],
  },
  {
    colCount: {
      xs: 1,
      sm: 2,
      md: 4,
      lg: 4,
    },
    caption: 'Selection Components',
    items: [
      allFields.selectBoxField,
      allFields.autocompleteField,
      allFields.lookupField,
      allFields.relatedItemsField,
    ],
  },
  {
    colCount: {
      xs: 1,
      sm: 2,
      md: 4,
      lg: 4,
    },
    caption: 'Boolean & Choice Components',
    items: [
      allFields.checkBoxField,
      allFields.switchField,
      allFields.radioGroupField,
      allFields.tagBoxField,
    ],
  },
  {
    colCount: {
      xs: 1,
      sm: 2,
      md: 3,
      lg: 3,
    },
    caption: 'Date & Time Components',
    items: [
      allFields.dateBoxField,
      allFields.dateTimeBoxField,
      allFields.timeBoxField,
      allFields.calendarField,
    ],
  },
  {
    colCount: {
      xs: 1,
      sm: 2,
      md: 3,
      lg: 3,
    },
    caption: 'Range & Color Components',
    items: [allFields.sliderField, allFields.rangeSliderField, allFields.colorBoxField],
  },
  {
    colCount: {
      xs: 1,
      sm: 1,
      md: 3,
      lg: 3,
    },
    caption: 'Advanced Components',
    items: [allFields.dropDownBoxField, allFields.htmlEditorField, allFields.imageField],
  },
  {
    colCount: {
      xs: 1,
      sm: 1,
      md: 2,
      lg: 2,
    },
    caption: 'Original Fields',
    items: [allFields.id, allFields.tag],
  },
  {
    caption: 'Action Buttons',
    items: [
      // Action buttons will be handled by the actions prop in the Form component
      // This section serves as a visual separator and grouping for the buttons
    ],
  },
];

export default fields;
